import { onSlideChange } from './slides.ts';
import { onTranslationsApplied, t } from './localization.ts';
import type { ProgressStepId } from './types.ts';

const PROGRESS_SLIDE_IDS = [
  'free-materials',
  'ab-testing',
  'hooked',
  'cookies',
  'data',
  'tricks',
  'reflection',
  'prove-me-wrong',
  'cta2',
];

const ProgressStep = Object.freeze({
  FREE_MATERIALS_VIEWED: 'free-materials-viewed',
  AB_TEST_CHOICE: 'ab-test-choice',
  HOOKED_STAGE_TRIGGER: 'hooked-stage-trigger',
  HOOKED_STAGE_ACTION: 'hooked-stage-action',
  HOOKED_STAGE_REWARD: 'hooked-stage-reward',
  HOOKED_STAGE_INVESTMENT: 'hooked-stage-investment',
  HOOKED_VIDEO: 'hooked-video',
  COOKIES_VISITED: 'cookies-visited',
  GUESS_LOCATION: 'guess-location',
  TRICKS_VISITED: 'tricks-visited',
  REFLECTION_VISITED: 'reflection-visited',
  COMMENT_POSTED: 'comment-posted',
  SHARE_BUTTON: 'share-button',
  CTA2_BUTTON: 'cta2-button',
});

const STEP_SEQUENCE = [
  ProgressStep.FREE_MATERIALS_VIEWED,
  ProgressStep.AB_TEST_CHOICE,
  ProgressStep.HOOKED_STAGE_TRIGGER,
  ProgressStep.HOOKED_STAGE_ACTION,
  ProgressStep.HOOKED_STAGE_REWARD,
  ProgressStep.HOOKED_STAGE_INVESTMENT,
  ProgressStep.HOOKED_VIDEO,
  ProgressStep.COOKIES_VISITED,
  ProgressStep.GUESS_LOCATION,
  ProgressStep.TRICKS_VISITED,
  ProgressStep.REFLECTION_VISITED,
  ProgressStep.COMMENT_POSTED,
  ProgressStep.SHARE_BUTTON,
  ProgressStep.CTA2_BUTTON,
];

const SLIDE_STEPS = {
  'free-materials': [ProgressStep.FREE_MATERIALS_VIEWED],
  'ab-testing': [ProgressStep.AB_TEST_CHOICE],
  hooked: [
    ProgressStep.HOOKED_STAGE_TRIGGER,
    ProgressStep.HOOKED_STAGE_ACTION,
    ProgressStep.HOOKED_STAGE_REWARD,
    ProgressStep.HOOKED_STAGE_INVESTMENT,
    ProgressStep.HOOKED_VIDEO,
  ],
  cookies: [ProgressStep.COOKIES_VISITED],
  data: [ProgressStep.GUESS_LOCATION],
  tricks: [ProgressStep.TRICKS_VISITED],
  reflection: [ProgressStep.REFLECTION_VISITED],
  'prove-me-wrong': [ProgressStep.COMMENT_POSTED],
  cta2: [ProgressStep.SHARE_BUTTON, ProgressStep.CTA2_BUTTON],
};

const TOTAL_STEPS = STEP_SEQUENCE.length;
const COMPLETION_VALUES = new Set(Object.values(ProgressStep));

const blackCompletedSteps = new Set<ProgressStepId>();
const progressBars: Array<{
  wrapper: HTMLElement;
  status: HTMLElement;
  label: HTMLElement;
  fills: { gray: HTMLElement; black: HTMLElement };
  slideId: string;
}> = [];

let slidesCache: HTMLElement[] | null = null;
let initialized: boolean = false;

function ensureSlidesCache(): HTMLElement[] {
  if (!slidesCache) {
    slidesCache = Array.from(document.querySelectorAll('.slide'));
  }
  return slidesCache;
}

function formatStatus(completed: number): string {
  return `${completed} / ${TOTAL_STEPS}`;
}

function getRemainingStepsOnSlide(slideId: string): number {
  const slideSteps = (SLIDE_STEPS as Record<string, ProgressStepId[]>)[slideId] || [];
  const completedOnSlide = slideSteps.reduce(
    (acc: number, stepId: ProgressStepId) => (blackCompletedSteps.has(stepId) ? acc + 1 : acc),
    0
  );
  return Math.max(0, slideSteps.length - completedOnSlide);
}

function updateBarElements(): void {
  const blackCount = blackCompletedSteps.size;

  progressBars.forEach(({ wrapper, status, fills, slideId }) => {
    const grayFill = fills.gray;
    const blackFill = fills.black;

    const blackPercent = (blackCount / TOTAL_STEPS) * 100;
    blackFill.style.width = `${blackPercent}%`;

    const remaining = getRemainingStepsOnSlide(slideId);
    const grayCount = Math.min(TOTAL_STEPS, blackCount + remaining);
    const grayPercent = (grayCount / TOTAL_STEPS) * 100;
    grayFill.style.width = `${grayPercent}%`;

    wrapper.setAttribute('aria-valuenow', String(blackCount));
    wrapper.setAttribute('aria-valuetext', `${blackCount} of ${TOTAL_STEPS}`);
    status.textContent = formatStatus(blackCount);
  });
}

function markBlackStep(stepId: ProgressStepId): void {
  if (!COMPLETION_VALUES.has(stepId)) return;
  if (blackCompletedSteps.has(stepId)) return;
  blackCompletedSteps.add(stepId);
  updateBarElements();
}

function createBar(slideId: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'progress-tracker';
  wrapper.setAttribute('role', 'progressbar');
  wrapper.setAttribute('aria-valuemin', '0');
  wrapper.setAttribute('aria-valuemax', String(TOTAL_STEPS));
  wrapper.setAttribute('aria-valuenow', '0');
  wrapper.setAttribute('aria-valuetext', `0 of ${TOTAL_STEPS}`);

  const meta = document.createElement('div');
  meta.className = 'progress-tracker__meta';
  const label = document.createElement('span');
  label.className = 'progress-tracker__label';
  label.textContent = t('progress.label', 'Progress');
  const status = document.createElement('span');
  status.className = 'progress-tracker__status';
  status.setAttribute('aria-live', 'polite');
  status.textContent = formatStatus(0);
  meta.appendChild(label);
  meta.appendChild(status);

  const track = document.createElement('div');
  track.className = 'progress-tracker__track';

  const grayFill = document.createElement('div');
  grayFill.className = 'progress-tracker__fill progress-tracker__fill--gray';
  track.appendChild(grayFill);

  const blackFill = document.createElement('div');
  blackFill.className = 'progress-tracker__fill progress-tracker__fill--black';
  track.appendChild(blackFill);

  wrapper.appendChild(meta);
  wrapper.appendChild(track);

  progressBars.push({
    wrapper,
    status,
    label,
    fills: { gray: grayFill, black: blackFill },
    slideId,
  });

  return wrapper;
}

function handleSlideChange(index: number): void {
  const slides = ensureSlidesCache();
  const slide = slides[index];
  if (!slide) return;

  switch (slide.id) {
    case 'cookies':
      window.setTimeout(() => {
        markBlackStep(ProgressStep.COOKIES_VISITED);
      }, 1000);
      break;
    case 'free-materials':
      window.setTimeout(() => {
        markBlackStep(ProgressStep.FREE_MATERIALS_VIEWED);
      }, 1000);
      break;
    case 'reflection':
      markBlackStep(ProgressStep.REFLECTION_VISITED);
      break;
    default:
      break;
  }

  updateBarElements();
}

function initProgressTracker(): void {
  if (initialized) return;
  initialized = true;

  PROGRESS_SLIDE_IDS.forEach((id) => {
    const slide = document.getElementById(id);
    if (!slide) return;
    const slot = slide.querySelector('[data-progress-slot]');
    const bar = createBar(id);
    if (slot) {
      slot.innerHTML = '';
      slot.appendChild(bar);
    } else {
      slide.insertBefore(bar, slide.firstChild);
    }
  });

  onSlideChange((payload) => {
    handleSlideChange(payload.index);
  });

  const cta2Buttons = Array.from(document.querySelectorAll('#cta2 .cta button'));
  if (cta2Buttons.length) {
    cta2Buttons.forEach((button) => {
      button.addEventListener('click', () => {
        markBlackStep(ProgressStep.CTA2_BUTTON);
      });
    });
  }

  const shareButton = document.querySelector('#cta2 [data-panel-id="share"]');
  if (shareButton) {
    shareButton.addEventListener('click', () => {
      markBlackStep(ProgressStep.SHARE_BUTTON);
    });
  }

  const commentButton = document.getElementById('btnPostComment') as HTMLButtonElement | null;
  if (commentButton) {
    commentButton.addEventListener('click', () => {
      if (!commentButton.disabled) {
        markBlackStep(ProgressStep.COMMENT_POSTED);
      }
    });
  }

  updateBarElements();

  onTranslationsApplied(() => {
    progressBars.forEach(({ label: barLabel }) => {
      barLabel.textContent = t('progress.label', 'Progress');
    });
  });
}

export { initProgressTracker, markBlackStep as markProgressStep, ProgressStep };
